import { useState, useEffect, useCallback } from "react";
import {
  BarChart3, TrendingUp, Building2, Target, Filter, Download, RefreshCw,
  Loader2, ArrowUpRight, ArrowDownRight, Minus, MousePointerClick, Users,
  Star, Lock, Package, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Layers,
  Sparkles, ExternalLink, Plus, ChevronDown, ChevronUp, GlobeIcon, UserCheck,
  CalendarDays, Eye, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area,
} from "recharts";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEYWORD_TREND_COLORS = ["#194449", "#07641D", "#DC2626", "#B45309", "#0E7490", "#7C3AED", "#DB2777", "#16A34A"];
const COLORS = ["#194449", "#07641D", "#D4EC8E", "#B9DCD2", "#64748B", "#091F2C"];

function getAuthHeader() {
  const token = localStorage.getItem("admin_token") || localStorage.getItem("app_token");
  return { authorization: `Bearer ${token}` };
}

/* ── Shared UI atoms ──────────────────────────────────────────── */

function StatCard({ label, value, sub, icon: Icon, color = "#194449", trend, onClick }) {
  return (
    <div
      className={`bg-white rounded-xl border border-black/5 p-5 shadow-sm transition-all ${onClick ? "cursor-pointer hover:shadow-md hover:border-[#194449]/20" : ""}`}
      data-testid={`analytics-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <div className="text-2xl font-semibold text-[#091F2C]">{value}</div>
          <div className="text-xs text-[#64748B]">{label}</div>
          {sub && <div className="text-[10px] text-[#B9DCD2]">{sub}</div>}
        </div>
        {trend !== undefined && trend !== null && trend !== 0 && (
          <div className={`flex items-center gap-0.5 text-xs font-medium ${trend > 0 ? "text-emerald-600" : "text-red-500"}`}>
            {trend > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {Math.abs(trend)}%
          </div>
        )}
        {onClick && <Eye className="w-3.5 h-3.5 text-[#B9DCD2]" />}
      </div>
    </div>
  );
}

function FunnelBar({ stage, eventCount, uniqueUsers, pctOfTotal, dropoffPct }) {
  const label = stage.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const barWidth = Math.max(pctOfTotal, 8);
  return (
    <div className="flex items-center gap-4 group" data-testid={`funnel-stage-${stage}`}>
      <div className="w-44 text-sm text-[#091F2C] font-medium truncate">{label}</div>
      <div className="flex-1 relative">
        <div
          className="h-10 rounded-lg flex items-center px-3 transition-all duration-500"
          style={{ width: `${barWidth}%`, backgroundColor: "#194449", opacity: 0.15 + (pctOfTotal / 100) * 0.85 }}
        >
          <span className="text-xs font-semibold text-[#091F2C]">{uniqueUsers} users</span>
        </div>
      </div>
      <div className="w-16 text-right text-sm font-medium text-[#091F2C]">{pctOfTotal}%</div>
      <div className="w-20 text-right">
        {dropoffPct !== undefined && dropoffPct > 0 ? (
          <span className="text-xs text-red-500 font-medium flex items-center justify-end gap-0.5">
            <ArrowDownRight className="w-3 h-3" /> {dropoffPct}%
          </span>
        ) : dropoffPct === 0 ? (
          <span className="text-xs text-[#64748B]"><Minus className="w-3 h-3 inline" /></span>
        ) : null}
      </div>
    </div>
  );
}

/* ── Drill-down Dialog ──────────────────────────────────────────── */
function DrillDownDialog({ open, onClose, metric, region, roleBucket, days }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const METRIC_LABELS = {
    learning_plans: "Learning Plans Generated",
    incomplete_assessments: "Incomplete Assessments",
    resource_clicks: "Resource Clicks",
    unique_learners: "Unique Learners",
  };

  useEffect(() => {
    if (!open || !metric) return;
    setPage(1);
    fetchPage(1);
  }, [open, metric, region, roleBucket, days]);

  const fetchPage = async (p) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/analytics/drill-down`, {
        headers: getAuthHeader(),
        params: { metric, region, role_bucket: roleBucket, days, page: p, page_size: 20 },
      });
      setData(res.data);
      setPage(p);
    } catch {
      toast.error("Failed to load drill-down data");
    } finally {
      setLoading(false);
    }
  };

  const columns = {
    learning_plans: ["Session", "Region", "Role", "Hero Products", "Rating", "Feedback", "Date"],
    incomplete_assessments: ["Session", "Region", "Role", "Date"],
    resource_clicks: ["Resource ID", "Region", "Role", "Date"],
    unique_learners: ["LMS ID", "Name", "Region", "Role", "Plans", "Last Active"],
  };

  const renderRow = (row) => {
    if (metric === "learning_plans") {
      return (
        <TableRow key={row.session_id}>
          <TableCell className="text-xs font-mono">{(row.session_id || "").slice(0, 12)}...</TableCell>
          <TableCell className="text-xs">{row.region}</TableCell>
          <TableCell className="text-xs">{row.role || "—"}</TableCell>
          <TableCell className="text-xs max-w-[150px] truncate">{row.hero_products}</TableCell>
          <TableCell className="text-xs text-center">{row.star_rating ? `${row.star_rating}/5` : "—"}</TableCell>
          <TableCell className="text-xs max-w-[120px] truncate">{row.feedback || "—"}</TableCell>
          <TableCell className="text-xs text-[#64748B]">{(row.date || "").slice(0, 10)}</TableCell>
        </TableRow>
      );
    }
    if (metric === "incomplete_assessments") {
      return (
        <TableRow key={row.session_id}>
          <TableCell className="text-xs font-mono">{(row.session_id || "").slice(0, 12)}...</TableCell>
          <TableCell className="text-xs">{row.region}</TableCell>
          <TableCell className="text-xs">{row.role || "—"}</TableCell>
          <TableCell className="text-xs text-[#64748B]">{(row.date || "").slice(0, 10)}</TableCell>
        </TableRow>
      );
    }
    if (metric === "resource_clicks") {
      return (
        <TableRow key={row.event_id}>
          <TableCell className="text-xs font-mono">{row.resource_id}</TableCell>
          <TableCell className="text-xs">{row.region}</TableCell>
          <TableCell className="text-xs">{row.role || "—"}</TableCell>
          <TableCell className="text-xs text-[#64748B]">{(row.date || "").slice(0, 10)}</TableCell>
        </TableRow>
      );
    }
    if (metric === "unique_learners") {
      return (
        <TableRow key={row.user_id}>
          <TableCell className="text-xs font-mono">{row.lms_id || "—"}</TableCell>
          <TableCell className="text-xs">{row.name || "Unknown"}</TableCell>
          <TableCell className="text-xs">{row.region}</TableCell>
          <TableCell className="text-xs">{row.role || "—"}</TableCell>
          <TableCell className="text-xs text-right">{row.plans}</TableCell>
          <TableCell className="text-xs text-[#64748B]">{(row.last_active || "").slice(0, 10)}</TableCell>
        </TableRow>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="drill-down-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="w-4 h-4 text-[#194449]" />
            {METRIC_LABELS[metric] || metric} — Detail View
          </DialogTitle>
          <DialogDescription>
            {data?.total || 0} records — {region !== "all" ? region : "All Regions"}, {roleBucket !== "all" ? roleBucket.replace(/_/g, " ") : "All Roles"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#194449]" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {(columns[metric] || []).map(c => <TableHead key={c} className="text-xs">{c}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.rows || []).map(renderRow)}
                {data?.rows?.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#64748B] py-8">No data for selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between pt-3 border-t border-black/5">
            <span className="text-xs text-[#64748B]">Page {page} of {data.total_pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => fetchPage(page - 1)}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => fetchPage(page + 1)}>Next</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ── Main Analytics Component ─────────────────────────────────── */
export default function AdminAnalytics() {
  const [days, setDays] = useState("30");
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [activeAnalyticsTab, setActiveAnalyticsTab] = useState("overview");
  const [usage, setUsage] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [resources, setResources] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [learners, setLearners] = useState(null);
  const [ratings, setRatings] = useState(null);
  const [productAdoption, setProductAdoption] = useState(null);
  const [monthlyLearners, setMonthlyLearners] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [emergingKeywords, setEmergingKeywords] = useState(null);
  const [keywordPeriod, setKeywordPeriod] = useState("weekly");
  const [keywordArea, setKeywordArea] = useState("all");
  const [keywordRole, setKeywordRole] = useState("all");
  const [segmentation, setSegmentation] = useState(null);

  // Phase 2: Trends
  const [trends, setTrends] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState("monthly");
  const [regionFilter, setRegionFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");

  // Drill-down
  const [drillDown, setDrillDown] = useState({ open: false, metric: null });

  const adminRole = localStorage.getItem("admin_role") || "";
  const canSeePII = adminRole === "super_admin" || adminRole === "admin";

  // Coverage-gap dialog
  const [gapDialog, setGapDialog] = useState({ open: false, product: null });
  const [gapSuggestions, setGapSuggestions] = useState(null);
  const [gapLoading, setGapLoading] = useState(false);
  const [gapSelected, setGapSelected] = useState({});
  const [gapImporting, setGapImporting] = useState(false);

  const openGapSuggestions = async (product) => {
    setGapDialog({ open: true, product });
    setGapSuggestions(null);
    setGapSelected({});
    setGapLoading(true);
    try {
      const res = await axios.get(`${API}/admin/resources/gap-suggestions/${product.product_id}`, { headers: getAuthHeader() });
      setGapSuggestions(res.data);
      const sel = {};
      (res.data.suggestions || []).forEach((s) => { sel[s.resource_id] = true; });
      setGapSelected(sel);
    } catch {
      toast.error("Failed to load suggestions");
      setGapSuggestions({ count: 0, suggestions: [], message: "Failed to load suggestions." });
    } finally {
      setGapLoading(false);
    }
  };

  const importGapSelected = async () => {
    const ids = Object.keys(gapSelected).filter((k) => gapSelected[k]);
    if (ids.length === 0) { toast.error("Select at least one resource"); return; }
    setGapImporting(true);
    try {
      const res = await axios.post(`${API}/admin/resources/import-suggestions`,
        { resource_ids: ids, product_id: gapDialog.product?.product_id },
        { headers: getAuthHeader() });
      toast.success(res.data.message || `Imported ${res.data.imported}`, { duration: 7000 });
      setGapDialog({ open: false, product: null });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setGapImporting(false);
    }
  };

  // Fetch trends whenever filters change
  const fetchTrends = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/trends`, {
        headers: getAuthHeader(),
        params: { period: trendPeriod, region: regionFilter, role_bucket: roleFilter, days: parseInt(days) },
      });
      setTrends(res.data);
    } catch {
      // silently fail — trends are supplementary
    }
  }, [trendPeriod, regionFilter, roleFilter, days]);

  // Fetch emerging keywords whenever the period / area / role filters change
  const fetchEmergingKeywords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics/emerging-keywords`, {
        headers: getAuthHeader(),
        params: { days: parseInt(days), period: keywordPeriod, solution_area: keywordArea, role_bucket: keywordRole },
      });
      setEmergingKeywords(res.data);
    } catch {
      // silently fail — supplementary panel
    }
  }, [keywordPeriod, keywordArea, keywordRole, days]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const headers = getAuthHeader();
    const d = parseInt(days);
    try {
      const base = [
        axios.get(`${API}/admin/analytics/usage-overview?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/org-insights?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/assessment-outcomes?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/resource-impact?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/funnel?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/ratings-feedback?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/product-adoption?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/monthly-learners`, { headers }),
        axios.get(`${API}/admin/analytics/coverage-gaps`, { headers }),
        axios.get(`${API}/admin/analytics/segmentation?days=${d}`, { headers }),
        axios.get(`${API}/admin/analytics/trends`, { headers, params: { period: trendPeriod, region: regionFilter, role_bucket: roleFilter, days: d } }),
        axios.get(`${API}/admin/analytics/emerging-keywords`, { headers, params: { days: d, period: "weekly" } }),
      ];
      if (canSeePII) {
        base.push(axios.get(`${API}/admin/analytics/learners?days=${d}`, { headers }));
      }
      const results = await Promise.allSettled(base);
      const ok = (i) => results[i]?.status === "fulfilled" ? results[i].value.data : null;
      setUsage(ok(0));
      setOrgs(ok(1));
      setOutcomes(ok(2));
      setResources(ok(3));
      setFunnel(ok(4));
      setRatings(ok(5));
      setProductAdoption(ok(6));
      setMonthlyLearners(ok(7));
      setCoverage(ok(8));
      setSegmentation(ok(9));
      setTrends(ok(10));
      setEmergingKeywords(ok(11));
      if (canSeePII && ok(12)) setLearners(ok(12));
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [days, canSeePII, trendPeriod, regionFilter, roleFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Re-fetch trends when filters change (without full reload)
  useEffect(() => {
    if (!loading) fetchTrends();
  }, [trendPeriod, regionFilter, roleFilter, fetchTrends, loading]);

  // Re-fetch emerging keywords when the period / area / role filters change
  useEffect(() => {
    if (!loading) fetchEmergingKeywords();
  }, [keywordPeriod, keywordArea, keywordRole, fetchEmergingKeywords, loading]);

  const handleExport = async (type) => {
    try {
      const headers = getAuthHeader();
      const isExcel = type === "excel-overview" || type === "respondent-report";
      const res = await axios.get(`${API}/admin/analytics/export/${type}`, {
        headers,
        responseType: "blob",
        params: isExcel ? { days: 365 } : {},
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = isExcel
        ? `mygoals_${type}_${new Date().toISOString().slice(0, 10)}.xlsx`
        : `${type}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`${isExcel ? "Excel report" : type.replace(/-/g, " ")} exported`);
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading && initialLoad) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#194449]" />
      </div>
    );
  }

  // Prepare chart data
  const dailyData = (usage?.daily_series || []).slice(-14).map(d => ({
    date: d.date?.slice(5),
    views: d.assessment_viewed || 0,
    started: d.assessment_started || 0,
    completed: d.assessment_completed || 0,
    generated: d.plan_generated || 0,
  }));

  const roleData = outcomes ? Object.entries(outcomes.by_role).map(([role, data]) => ({
    name: role.charAt(0).toUpperCase() + role.slice(1),
    value: data.total,
    passRate: data.pass_rate,
  })) : [];

  const areaData = outcomes ? Object.entries(outcomes.by_solution_area).map(([area, data]) => ({
    name: area.replace(/_/g, " "),
    total: data.total,
    passed: data.passed,
  })) : [];

  const trendSeries = (trends?.series || []).map(s => ({
    ...s,
    period_label: s.period,
  }));

  return (
    <div className="space-y-6">
      {/* ── Global Controls ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-semibold text-[#091F2C]" style={{ fontFamily: "Outfit, sans-serif" }}>
          Analytics Dashboard
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Region Filter */}
          <Select value={regionFilter} onValueChange={setRegionFilter}>
            <SelectTrigger className="w-[130px] h-9 bg-white text-xs" data-testid="analytics-region-filter">
              <GlobeIcon className="w-3.5 h-3.5 mr-1 text-[#64748B]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              <SelectItem value="Americas">Americas</SelectItem>
              <SelectItem value="EMEA">EMEA</SelectItem>
              <SelectItem value="APAC">APAC</SelectItem>
            </SelectContent>
          </Select>
          {/* Role Filter */}
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[145px] h-9 bg-white text-xs" data-testid="analytics-role-filter">
              <UserCheck className="w-3.5 h-3.5 mr-1 text-[#64748B]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="technical">Technical</SelectItem>
              <SelectItem value="sales_presales">Sales/Pre-Sales</SelectItem>
              <SelectItem value="executive_sponsor">Executive</SelectItem>
              <SelectItem value="marketing_gtm">Marketing/GTM</SelectItem>
            </SelectContent>
          </Select>
          {/* Period */}
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[130px] h-9 bg-white text-xs" data-testid="analytics-period-select">
              <CalendarDays className="w-3.5 h-3.5 mr-1 text-[#64748B]" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={fetchAll} data-testid="analytics-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={() => handleExport("respondent-report")}
            data-testid="export-respondent-report-btn"
            className="gap-1.5 text-[#194449] border-[#194449]/30 hover:bg-[#194449]/5"
            title="Per-learner engagement + learning-plan report."
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Respondent Report
          </Button>
        </div>
      </div>

      {/* ── Active Filters Indicator ── */}
      {(regionFilter !== "all" || roleFilter !== "all") && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#64748B]">Active filters:</span>
          {regionFilter !== "all" && (
            <Badge className="bg-[#194449]/10 text-[#194449] border-0 gap-1 text-xs cursor-pointer" onClick={() => setRegionFilter("all")}>
              {regionFilter} <X className="w-3 h-3" />
            </Badge>
          )}
          {roleFilter !== "all" && (
            <Badge className="bg-[#07641D]/10 text-[#07641D] border-0 gap-1 text-xs cursor-pointer" onClick={() => setRoleFilter("all")}>
              {roleFilter.replace(/_/g, " ")} <X className="w-3 h-3" />
            </Badge>
          )}
        </div>
      )}

      {/* Analytics Sub-Tabs */}
      <Tabs value={activeAnalyticsTab} onValueChange={setActiveAnalyticsTab}>
        <TabsList className="bg-white border border-black/5 p-1 rounded-lg mb-6 flex-wrap">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-overview">
            <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Overview
          </TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-trends">
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Trends
          </TabsTrigger>
          <TabsTrigger value="orgs" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-orgs">
            <Building2 className="w-3.5 h-3.5 mr-1.5" /> Organizations
          </TabsTrigger>
          <TabsTrigger value="outcomes" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-outcomes">
            <Target className="w-3.5 h-3.5 mr-1.5" /> Outcomes
          </TabsTrigger>
          <TabsTrigger value="resources" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-resources">
            <MousePointerClick className="w-3.5 h-3.5 mr-1.5" /> Resources
          </TabsTrigger>
          <TabsTrigger value="funnel" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-funnel">
            <Filter className="w-3.5 h-3.5 mr-1.5" /> Funnel
          </TabsTrigger>
          <TabsTrigger value="adoption" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-adoption">
            <Package className="w-3.5 h-3.5 mr-1.5" /> Product Adoption
          </TabsTrigger>
          <TabsTrigger value="feedback" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-feedback">
            <Star className="w-3.5 h-3.5 mr-1.5" /> Feedback
          </TabsTrigger>
          <TabsTrigger value="coverage" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-coverage">
            <Layers className="w-3.5 h-3.5 mr-1.5" /> Coverage Gaps
          </TabsTrigger>
          <TabsTrigger value="segmentation" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-segmentation">
            <Building2 className="w-3.5 h-3.5 mr-1.5" /> Segmentation
          </TabsTrigger>
          {canSeePII && (
            <TabsTrigger value="learners" className="data-[state=active]:bg-[#194449] data-[state=active]:text-white rounded-md text-xs" data-testid="analytics-tab-learners">
              <Lock className="w-3.5 h-3.5 mr-1.5" /> Learners (PII)
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Overview (Standardized Metrics) ── */}
        <TabsContent value="overview">
          {/* Standardized metric cards — clickable for drill-down */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard
              label="Learning Plans"
              value={trends?.current?.learning_plans ?? (usage?.by_type?.plan_generated || 0)}
              icon={TrendingUp}
              color="#194449"
              trend={trends?.comparison?.learning_plans}
              onClick={() => setDrillDown({ open: true, metric: "learning_plans" })}
            />
            <StatCard
              label="Incomplete Assessments"
              value={trends?.current?.incomplete_assessments ?? 0}
              icon={Target}
              color="#F59E0B"
              trend={trends?.comparison?.incomplete_assessments}
              sub="Started but didn't generate a plan"
              onClick={() => setDrillDown({ open: true, metric: "incomplete_assessments" })}
            />
            <StatCard
              label="Resource Clicks"
              value={trends?.current?.resource_clicks ?? (usage?.by_type?.resource_clicked || 0)}
              icon={MousePointerClick}
              color="#07641D"
              trend={trends?.comparison?.resource_clicks}
              onClick={() => setDrillDown({ open: true, metric: "resource_clicks" })}
            />
            <StatCard
              label="Unique Learners"
              value={trends?.current?.unique_learners ?? (usage?.unique_users || 0)}
              icon={Users}
              color="#194449"
              trend={trends?.comparison?.unique_learners}
              onClick={() => setDrillDown({ open: true, metric: "unique_learners" })}
            />
          </div>

          {/* Regional + Role breakdowns side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Regional Breakdown */}
            <div className="bg-white rounded-xl border border-black/5 p-5 shadow-sm">
              <h3 className="font-medium text-[#091F2C] text-sm mb-4 flex items-center gap-2">
                <GlobeIcon className="w-4 h-4 text-[#194449]" /> Regional Breakdown
              </h3>
              {(trends?.by_region || []).length > 0 ? (
                <div className="space-y-3">
                  {(trends?.by_region || []).map((r) => {
                    const maxPlans = Math.max(1, ...(trends?.by_region || []).map(x => x.learning_plans));
                    const pct = Math.round((r.learning_plans / maxPlans) * 100);
                    return (
                      <div key={r.region} className="flex items-center gap-3">
                        <span className="text-xs text-[#091F2C] font-medium w-20">{r.region}</span>
                        <div className="flex-1 h-3 bg-[#F4F3F5] rounded-full overflow-hidden">
                          <div className="h-full bg-[#194449] rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-mono text-[#091F2C] w-12 text-right">{r.learning_plans}</span>
                        <span className="text-[10px] text-[#64748B] w-16 text-right">{r.unique_learners} users</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-[#64748B] text-center py-6">No regional data — learner regions will appear as users authenticate via SSO.</p>
              )}
            </div>

            {/* Role Breakdown */}
            <div className="bg-white rounded-xl border border-black/5 p-5 shadow-sm">
              <h3 className="font-medium text-[#091F2C] text-sm mb-4 flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-[#194449]" /> Role Breakdown
              </h3>
              {(trends?.by_role || []).length > 0 ? (
                <div className="space-y-3">
                  {(trends?.by_role || []).map((r) => {
                    const maxPlans = Math.max(1, ...(trends?.by_role || []).map(x => x.learning_plans));
                    const pct = Math.round((r.learning_plans / maxPlans) * 100);
                    const colors = { Technical: "#194449", "Sales/Pre-Sales": "#07641D", Executive: "#B9DCD2", "Marketing/GTM": "#64748B" };
                    return (
                      <div key={r.role_key} className="flex items-center gap-3">
                        <span className="text-xs text-[#091F2C] font-medium w-24 truncate">{r.role}</span>
                        <div className="flex-1 h-3 bg-[#F4F3F5] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[r.role] || "#194449" }} />
                        </div>
                        <span className="text-xs font-mono text-[#091F2C] w-12 text-right">{r.learning_plans}</span>
                        <span className="text-[10px] text-[#64748B] w-16 text-right">{r.unique_learners} users</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-[#64748B] text-center py-6">No role data available.</p>
              )}
            </div>
          </div>

          {/* Daily Activity — shrunk */}
          <div className="bg-white rounded-xl border border-black/5 p-5 shadow-sm mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-[#091F2C] text-sm">Daily Activity (last 14 days)</h3>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExport("excel-overview")} data-testid="export-excel-btn" className="gap-1.5 text-[#194449] border-[#194449]/30 hover:bg-[#194449]/5 h-7 text-xs">
                  <FileSpreadsheet className="w-3 h-3" /> Excel
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleExport("monthly-summary")} data-testid="export-monthly-btn" className="h-7 text-xs">
                  <Download className="w-3 h-3 mr-1" /> CSV
                </Button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 10, fill: "#64748B" }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="generated" name="Plans Generated" fill="#194449" radius={[2, 2, 0, 0]} />
                <Bar dataKey="started" name="Started" fill="#D4EC8E" radius={[2, 2, 0, 0]} />
                <Bar dataKey="views" name="Viewed" fill="#B9DCD2" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Event Type Breakdown */}
          <div className="bg-white rounded-xl border border-black/5 p-5 shadow-sm">
            <h3 className="font-medium text-[#091F2C] text-sm mb-3">Event Type Breakdown</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(usage?.by_type || {}).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between p-2.5 rounded-lg bg-[#F4F3F5]">
                  <span className="text-xs text-[#64748B]">{type.replace(/_/g, " ")}</span>
                  <span className="text-sm font-semibold text-[#091F2C]">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* ── Trends Tab (NEW — Phase 2) ── */}
        <TabsContent value="trends">
          <div className="space-y-6">
            {/* Period selector */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#64748B]">Aggregation:</span>
              {["weekly", "monthly", "quarterly"].map(p => (
                <Button
                  key={p}
                  variant={trendPeriod === p ? "default" : "outline"}
                  size="sm"
                  className={`text-xs h-7 ${trendPeriod === p ? "bg-[#194449] text-white" : ""}`}
                  data-testid={`trend-period-${p}`}
                  onClick={() => setTrendPeriod(p)}
                >
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </Button>
              ))}
            </div>

            {/* Comparison strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                label="Learning Plans"
                value={trends?.current?.learning_plans ?? 0}
                icon={TrendingUp}
                color="#194449"
                trend={trends?.comparison?.learning_plans}
                sub={`vs ${trends?.previous?.learning_plans ?? 0} prev period`}
              />
              <StatCard
                label="Incomplete Assessments"
                value={trends?.current?.incomplete_assessments ?? 0}
                icon={Target}
                color="#F59E0B"
                trend={trends?.comparison?.incomplete_assessments}
                sub={`vs ${trends?.previous?.incomplete_assessments ?? 0} prev period`}
              />
              <StatCard
                label="Resource Clicks"
                value={trends?.current?.resource_clicks ?? 0}
                icon={MousePointerClick}
                color="#07641D"
                trend={trends?.comparison?.resource_clicks}
                sub={`vs ${trends?.previous?.resource_clicks ?? 0} prev period`}
              />
              <StatCard
                label="Unique Learners"
                value={trends?.current?.unique_learners ?? 0}
                icon={Users}
                color="#194449"
                trend={trends?.comparison?.unique_learners}
                sub={`vs ${trends?.previous?.unique_learners ?? 0} prev period`}
              />
            </div>

            {/* Trend line chart */}
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <h3 className="font-medium text-[#091F2C] mb-4">
                {trendPeriod.charAt(0).toUpperCase() + trendPeriod.slice(1)} Trends
              </h3>
              {trendSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={trendSeries}>
                    <defs>
                      <linearGradient id="gradPlans" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#194449" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#194449" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradLearners" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#07641D" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#07641D" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="period_label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="learning_plans" name="Learning Plans" stroke="#194449" fill="url(#gradPlans)" strokeWidth={2} dot={{ r: 3 }} />
                    <Area type="monotone" dataKey="unique_learners" name="Unique Learners" stroke="#07641D" fill="url(#gradLearners)" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="resource_clicks" name="Resource Clicks" stroke="#B9DCD2" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#64748B] text-center py-8">No trend data for the selected period and filters.</p>
              )}
            </div>

            {/* Trend data table */}
            {trendSeries.length > 0 && (
              <div className="bg-white rounded-xl border border-black/5 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-black/5">
                  <h3 className="font-medium text-[#091F2C] text-sm">Period Detail</h3>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Period</TableHead>
                      <TableHead className="text-xs text-right">Learning Plans</TableHead>
                      <TableHead className="text-xs text-right">Incomplete</TableHead>
                      <TableHead className="text-xs text-right">Clicks</TableHead>
                      <TableHead className="text-xs text-right">Learners</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trendSeries.map(s => (
                      <TableRow key={s.period}>
                        <TableCell className="text-xs font-medium">{s.period}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.learning_plans}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.incomplete_assessments}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.resource_clicks}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{s.unique_learners}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Organization Insights ── */}
        <TabsContent value="orgs">
          <div className="bg-white rounded-xl border border-black/5 shadow-sm">
            <div className="p-5 border-b border-black/5 flex items-center justify-between">
              <h3 className="font-medium text-[#091F2C]">Organizations ({orgs?.organizations?.length || 0})</h3>
              <Button variant="ghost" size="sm" onClick={() => handleExport("org-report")} data-testid="export-org-btn">
                <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
              </Button>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Domain</TableHead>
                    <TableHead className="text-xs">Users</TableHead>
                    <TableHead className="text-xs">Events</TableHead>
                    <TableHead className="text-xs">Plans</TableHead>
                    <TableHead className="text-xs">Completed</TableHead>
                    <TableHead className="text-xs">Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(orgs?.organizations || []).map((org, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs font-medium">{org.domain}</TableCell>
                      <TableCell className="text-xs">{org.unique_users}</TableCell>
                      <TableCell className="text-xs">{org.total_events}</TableCell>
                      <TableCell className="text-xs">{org.plans_generated}</TableCell>
                      <TableCell className="text-xs">{org.assessments_completed}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${org.completion_rate > 50 ? "bg-[#D4EC8E] text-[#091F2C] border-0" : "bg-[#F4F3F5] text-[#64748B] border-0"}`}>
                          {org.completion_rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!orgs?.organizations?.length) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-sm text-[#64748B] py-8">
                        No organization data yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* ── Assessment Outcomes ── */}
        <TabsContent value="outcomes">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Sessions" value={outcomes?.total_sessions || 0} icon={Target} />
            <StatCard label="Pass Rate" value={`${outcomes?.pass_rate || 0}%`} icon={TrendingUp} color="#07641D" />
            <StatCard label="Avg Gen Time" value={`${((outcomes?.avg_generation_ms || 0) / 1000).toFixed(1)}s`} icon={BarChart3} color="#194449" />
            <StatCard label="Refine Rate" value={`${outcomes?.refine_rate || 0}%`} icon={Filter} color="#07641D" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#091F2C] text-sm">By Role</h3>
                <Button variant="ghost" size="sm" onClick={() => handleExport("assessment-outcomes")} data-testid="export-outcomes-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={roleData} cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {roleData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <h3 className="font-medium text-[#091F2C] text-sm mb-4">By Solution Area</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={areaData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Bar dataKey="total" name="Total" fill="#B9DCD2" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="passed" name="Passed" fill="#194449" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {outcomes?.top_skill_focus_tags && Object.keys(outcomes.top_skill_focus_tags).length > 0 && (
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <h3 className="font-medium text-[#091F2C] text-sm mb-4">Top Skill Focus Tags</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(outcomes.top_skill_focus_tags).map(([tag, count]) => (
                  <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#194449]/10 text-[#194449] text-xs font-medium">
                    {tag} <span className="bg-[#194449] text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px]">{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Resource Impact ── */}
        <TabsContent value="resources">
          <div className="bg-white rounded-xl border border-black/5 shadow-sm">
            <div className="p-5 border-b border-black/5">
              <h3 className="font-medium text-[#091F2C]">Resource Impact ({resources?.resources?.length || 0})</h3>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Resource</TableHead>
                    <TableHead className="text-xs">Type</TableHead>
                    <TableHead className="text-xs text-center">Rec. Total</TableHead>
                    <TableHead className="text-xs text-center">Start</TableHead>
                    <TableHead className="text-xs text-center">Next</TableHead>
                    <TableHead className="text-xs text-center">Optional</TableHead>
                    <TableHead className="text-xs text-center">Clicks</TableHead>
                    <TableHead className="text-xs text-center">CTR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(resources?.resources || []).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs max-w-[200px]">
                        <div className="font-medium truncate">{r.title}</div>
                        <div className="text-[10px] text-[#64748B] font-mono">{r.resource_id}</div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="text-[10px]">{r.resource_type}</Badge></TableCell>
                      <TableCell className="text-xs text-center font-semibold">{r.recommended_total}</TableCell>
                      <TableCell className="text-xs text-center">{r.recommended_start}</TableCell>
                      <TableCell className="text-xs text-center">{r.recommended_next}</TableCell>
                      <TableCell className="text-xs text-center">{r.recommended_optional}</TableCell>
                      <TableCell className="text-xs text-center font-semibold">{r.clicks}</TableCell>
                      <TableCell className="text-xs text-center">
                        <Badge className={`text-xs ${r.click_through_rate > 20 ? "bg-[#D4EC8E] text-[#091F2C] border-0" : "bg-[#F4F3F5] text-[#64748B] border-0"}`}>
                          {r.click_through_rate}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!resources?.resources?.length) && (
                    <TableRow><TableCell colSpan={8} className="text-center text-sm text-[#64748B] py-8">No resource impact data yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        {/* ── Funnel ── */}
        <TabsContent value="funnel">
          <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-medium text-[#091F2C]">Assessment Funnel</h3>
                <p className="text-xs text-[#64748B] mt-1">{funnel?.total_unique_users || 0} unique users in period</p>
              </div>
            </div>
            <div className="space-y-3">
              {(funnel?.funnel || []).map((stage) => (
                <FunnelBar key={stage.stage} stage={stage.stage} eventCount={stage.event_count} uniqueUsers={stage.unique_users} pctOfTotal={stage.pct_of_total} dropoffPct={stage.dropoff_pct} maxUsers={funnel?.total_unique_users || 1} />
              ))}
            </div>
            {(!funnel?.funnel?.length || funnel.funnel.every(s => s.unique_users === 0)) && (
              <p className="text-sm text-[#64748B] text-center py-8">No funnel data yet.</p>
            )}
          </div>
        </TabsContent>

        {/* ── Product Adoption ── */}
        <TabsContent value="adoption">
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="Total Plans" value={productAdoption?.total_plans || 0} icon={Target} color="#194449" />
              <StatCard label="Unique Products Used" value={(productAdoption?.by_product || []).length} icon={Package} color="#07641D" />
              <StatCard label="Top Product" value={(productAdoption?.by_product || [])[0]?.product?.replace(/_/g, " ")?.slice(0, 20) || "—"} icon={TrendingUp} color="#B9DCD2" />
            </div>
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <h3 className="font-medium text-[#091F2C] mb-4">Plans Generated by Hero Product</h3>
              {(productAdoption?.by_product || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={(productAdoption.by_product || []).slice(0, 15).map(p => ({ name: p.product.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()).slice(0, 22), plans: p.plans }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="plans" fill="#194449" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#64748B] text-center py-8">No product adoption data yet.</p>
              )}
            </div>
            {(monthlyLearners?.series || []).length > 0 && (
              <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
                <h3 className="font-medium text-[#091F2C] mb-4">Monthly Unique Learners</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={monthlyLearners.series}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="unique_learners" stroke="#07641D" strokeWidth={2} dot={{ r: 3 }} name="Learners" />
                    <Line type="monotone" dataKey="plans_generated" stroke="#194449" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 5" name="Plans" />
                    <Legend />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Feedback & Ratings ── */}
        <TabsContent value="feedback">
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Ratings" value={ratings?.total_ratings || 0} icon={Star} color="#194449" />
              <StatCard label="Avg Rating" value={ratings?.avg_rating ? `${ratings.avg_rating} / 5` : "—"} icon={TrendingUp} color="#07641D" />
              <StatCard label="5-Star Responses" value={ratings?.distribution?.[5] || 0} icon={Star} color="#D4EC8E" />
              <StatCard label="Feedback Texts" value={(ratings?.recent_feedback || []).length} icon={Users} color="#B9DCD2" />
            </div>
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <h3 className="font-medium text-[#091F2C] mb-4">Rating Distribution</h3>
              {ratings?.total_ratings > 0 ? (
                <div className="space-y-3 max-w-lg">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = ratings?.distribution?.[star] || 0;
                    const pct = ratings?.total_ratings ? Math.round((count / ratings.total_ratings) * 100) : 0;
                    return (
                      <div key={star} className="flex items-center gap-3">
                        <span className="text-sm text-[#091F2C] w-12">{star} star{star !== 1 ? "s" : ""}</span>
                        <div className="flex-1 bg-[#F4F3F5] rounded-full h-3">
                          <div className="h-3 rounded-full bg-[#07641D] transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-[#64748B] w-12 text-right">{count} ({pct}%)</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-[#64748B] text-center py-8">No ratings submitted yet.</p>
              )}
            </div>
            {(ratings?.recent_feedback || []).length > 0 && (
              <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
                <h3 className="font-medium text-[#091F2C] mb-4">Recent Feedback</h3>
                <div className="space-y-3">
                  {(ratings.recent_feedback || []).slice(0, 20).map((fb, i) => (
                    <div key={i} className="border border-[#F4F3F5] rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, j) => (
                            <Star key={j} className={`w-3.5 h-3.5 ${j < fb.rating ? "text-yellow-400 fill-yellow-400" : "text-[#E2E8F0]"}`} />
                          ))}
                        </div>
                        {fb.role && <Badge variant="secondary" className="text-[10px] bg-[#F4F3F5]">{fb.role.replace(/_/g, " ")}</Badge>}
                        <span className="text-[10px] text-[#64748B] ml-auto">{fb.date?.slice(0, 10)}</span>
                      </div>
                      <p className="text-sm text-[#091F2C] leading-relaxed">{fb.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Learners PII (admin-only) ── */}
        {canSeePII && (
          <TabsContent value="learners">
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs">
                <Lock className="w-4 h-4 shrink-0" />
                PII data — restricted view. Visible to authorized admins only.
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <StatCard label="Total Accessed Learners" value={learners?.total_learners || 0} icon={Users} color="#194449" />
                <StatCard label="Plans Generated (period)" value={outcomes?.total_sessions || 0} icon={Target} color="#07641D" />
                <StatCard label="With Ratings" value={ratings?.total_ratings || 0} icon={Star} color="#B9DCD2" />
              </div>
              <div className="bg-white rounded-xl border border-black/5 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-black/5">
                  <h3 className="font-medium text-[#091F2C]">Learner Activity ({learners?.total_learners || 0})</h3>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Email</TableHead>
                        <TableHead className="text-xs">LMS ID</TableHead>
                        <TableHead className="text-xs">Partner Type</TableHead>
                        <TableHead className="text-xs">Partner Name</TableHead>
                        <TableHead className="text-xs">Region</TableHead>
                        <TableHead className="text-xs text-right">Plans</TableHead>
                        <TableHead className="text-xs text-right">Avg Rating</TableHead>
                        <TableHead className="text-xs">Last Active</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(learners?.learners || []).map((l) => (
                        <TableRow key={l.user_id} data-testid={`learner-row-${l.user_id}`}>
                          <TableCell className="text-sm font-medium">{l.name || "Unknown"}</TableCell>
                          <TableCell className="text-xs text-[#64748B] font-mono">{l.email || "—"}</TableCell>
                          <TableCell className="text-xs font-mono text-[#64748B]">{l.levelup_id || "—"}</TableCell>
                          <TableCell className="text-xs">
                            {l.domain_type ? <Badge variant="secondary" className="text-[10px]">{l.domain_type}</Badge> : "—"}
                          </TableCell>
                          <TableCell className="text-xs">{l.partner_name || "—"}</TableCell>
                          <TableCell className="text-xs">{l.region || "—"}</TableCell>
                          <TableCell className="text-sm text-right">{l.plans_generated}</TableCell>
                          <TableCell className="text-sm text-right">{l.avg_rating ? `${l.avg_rating}` : "—"}</TableCell>
                          <TableCell className="text-xs text-[#64748B]">{l.last_active?.slice(0, 10) || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {!learners?.learners?.length && (
                        <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#64748B] py-8">No learner data for this period.</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {/* ── Coverage Gaps ── */}
        <TabsContent value="coverage">
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Published Resources" value={coverage?.total_resources || 0} icon={Layers} color="#194449" />
              <StatCard label="Products with Gaps" value={`${coverage?.gap_products_count || 0} / ${coverage?.total_hero_products || 0}`} icon={AlertTriangle} color={coverage?.gap_products_count > 0 ? "#DC2626" : "#07641D"} />
              <StatCard label="AI-Tagged Resources" value={`${coverage?.ai_tag_coverage_pct || 0}%`} icon={CheckCircle2} color={coverage?.ai_tag_coverage_pct > 50 ? "#07641D" : "#F59E0B"} />
              <StatCard label="Zero-Resource Products" value={coverage?.critical_products_count || 0} icon={XCircle} color={coverage?.critical_products_count > 0 ? "#DC2626" : "#07641D"} />
            </div>
            {coverage?.gap_products_count > 0 && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">{coverage.gap_products_count} of {coverage.total_hero_products} hero products have fewer than {coverage.threshold} resources</p>
                  <p className="text-xs text-amber-700 mt-1">Upload and publish new resources for the flagged products.{coverage.critical_products_count > 0 && ` ${coverage.critical_products_count} products have zero resources.`}</p>
                </div>
              </div>
            )}
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <h3 className="font-medium text-[#091F2C] mb-4 flex items-center gap-2">
                <Layers className="w-4 h-4 text-[#194449]" /> Resource Coverage by Hero Product
                <span className="ml-auto text-xs text-[#64748B] font-normal">threshold: {coverage?.threshold || 5} resources</span>
              </h3>
              <div className="overflow-x-auto">
                <div style={{ minWidth: 700, height: Math.max(500, (coverage?.by_product?.length || 0) * 26 + 60) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={(coverage?.by_product || []).map(p => ({ name: p.display_name, resources: p.total, ai_tagged: p.ai_tagged, gap: p.gap, severity: p.gap_severity }))} margin={{ top: 5, right: 80, left: 200, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={190} />
                      <Tooltip formatter={(val, name) => [val, name === "resources" ? "Total Resources" : "AI Tagged"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="resources" name="Total Resources" radius={[0, 4, 4, 0]}>
                        {(coverage?.by_product || []).map((p, idx) => (
                          <Cell key={idx} fill={p.gap_severity === "critical" ? "#EF4444" : p.gap_severity === "low" ? "#F59E0B" : "#194449"} fillOpacity={0.85} />
                        ))}
                      </Bar>
                      <Bar dataKey="ai_tagged" name="AI Tagged" fill="#B9DCD2" fillOpacity={0.9} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-[#64748B]">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#EF4444] inline-block" /> Zero resources</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#F59E0B] inline-block" /> Below threshold</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#194449] inline-block" /> Covered</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#B9DCD2] inline-block" /> AI-tagged</span>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-black/5 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-black/5">
                <h3 className="font-medium text-[#091F2C]">Hero Product Details</h3>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Product</TableHead>
                      <TableHead className="text-xs text-right">Resources</TableHead>
                      <TableHead className="text-xs text-right">AI Tagged</TableHead>
                      <TableHead className="text-xs text-right">AI Coverage</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(coverage?.by_product || []).map((p) => (
                      <TableRow key={p.product_id} data-testid={`coverage-row-${p.product_id}`}>
                        <TableCell className="text-sm font-medium">{p.display_name}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{p.total}</TableCell>
                        <TableCell className="text-sm text-right font-mono">{p.ai_tagged}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 rounded-full bg-[#F4F3F5] overflow-hidden">
                              <div className="h-full bg-[#B9DCD2] rounded-full" style={{ width: `${p.ai_coverage_pct}%` }} />
                            </div>
                            <span className="text-xs text-[#64748B] w-8 text-right">{p.ai_coverage_pct}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {p.gap_severity === "critical" ? (
                            <Badge className="bg-red-100 text-red-700 border-0 text-xs gap-1"><XCircle className="w-3 h-3" /> No resources</Badge>
                          ) : p.gap_severity === "low" ? (
                            <Badge className="bg-amber-100 text-amber-700 border-0 text-xs gap-1"><AlertTriangle className="w-3 h-3" /> Below threshold</Badge>
                          ) : (
                            <Badge className="bg-[#D4EC8E] text-[#091F2C] border-0 text-xs gap-1"><CheckCircle2 className="w-3 h-3" /> Covered</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {p.gap && (
                            <Button variant="outline" size="sm" onClick={() => openGapSuggestions(p)} data-testid={`gap-suggest-btn-${p.product_id}`} className="h-7 gap-1.5 text-[#194449] border-[#194449]/30 hover:bg-[#194449]/5 text-xs">
                              <Sparkles className="w-3 h-3" /> Suggested Content
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
                <h3 className="font-medium text-[#091F2C] mb-4">Resource Format Distribution</h3>
                <div className="space-y-2.5">
                  {(coverage?.by_format || []).slice(0, 10).map((f) => {
                    const pct = coverage?.total_resources ? Math.round(f.total / coverage.total_resources * 100) : 0;
                    return (
                      <div key={f.format} className="flex items-center gap-3">
                        <span className="text-xs text-[#64748B] w-28 truncate" title={f.format}>{f.format || "Unknown"}</span>
                        <div className="flex-1 h-2 bg-[#F4F3F5] rounded-full overflow-hidden">
                          <div className="h-full bg-[#194449] rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-mono text-[#091F2C] w-8 text-right">{f.total}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm flex flex-col justify-between">
                <h3 className="font-medium text-[#091F2C] mb-4">AI Tagging Progress</h3>
                <div className="flex items-center justify-center my-4">
                  <div className="relative w-32 h-32">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#F4F3F5" strokeWidth="3" />
                      <circle cx="18" cy="18" r="15.9155" fill="none" stroke={coverage?.ai_tag_coverage_pct >= 80 ? "#07641D" : coverage?.ai_tag_coverage_pct >= 40 ? "#194449" : "#F59E0B"} strokeWidth="3" strokeDasharray={`${coverage?.ai_tag_coverage_pct || 0} 100`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-[#091F2C]">{coverage?.ai_tag_coverage_pct || 0}%</span>
                      <span className="text-[10px] text-[#64748B]">tagged</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#64748B]">AI-tagged</span>
                    <span className="font-medium text-[#07641D]">{coverage?.total_ai_tagged || 0}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-[#64748B]">Not tagged yet</span>
                    <span className="font-medium text-amber-600">{coverage?.total_untagged || 0}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-black/5 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                <h3 className="font-medium text-[#091F2C] flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#194449]" /> Emerging Keyword Requests
                </h3>
                <div className="flex items-center gap-2">
                  {["weekly", "monthly"].map(p => (
                    <Button
                      key={p}
                      variant={keywordPeriod === p ? "default" : "outline"}
                      size="sm"
                      className={`text-xs h-7 ${keywordPeriod === p ? "bg-[#194449] text-white" : ""}`}
                      data-testid={`keyword-period-${p}`}
                      onClick={() => setKeywordPeriod(p)}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-[#64748B] mb-4">
                Product-relevant terms learners typed in "skilling requirements" — certification codes, named Microsoft
                products and adjacent technologies only (generic words are filtered out). Grouped by the Solution Area
                and Primary Role the learner passed through when generating their plan.
                {" "}{emergingKeywords?.sessions_with_signal || 0} of {emergingKeywords?.sessions_with_free_text || 0} free-text sessions carry a signal.
              </p>

              {/* Solution Area + Role filters */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="text-xs text-[#64748B] mr-1">Solution Area:</span>
                <Button
                  variant={keywordArea === "all" ? "default" : "outline"} size="sm"
                  className={`text-xs h-7 ${keywordArea === "all" ? "bg-[#194449] text-white" : ""}`}
                  data-testid="keyword-area-all" onClick={() => setKeywordArea("all")}
                >All</Button>
                {(emergingKeywords?.available_solution_areas || []).map((a) => (
                  <Button
                    key={a.id}
                    variant={keywordArea === a.id ? "default" : "outline"} size="sm"
                    className={`text-xs h-7 ${keywordArea === a.id ? "bg-[#194449] text-white" : ""}`}
                    data-testid={`keyword-area-${a.id}`} onClick={() => setKeywordArea(a.id)}
                  >{a.label}</Button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="text-xs text-[#64748B] mr-1">Primary Role:</span>
                <Button
                  variant={keywordRole === "all" ? "default" : "outline"} size="sm"
                  className={`text-xs h-7 ${keywordRole === "all" ? "bg-[#194449] text-white" : ""}`}
                  data-testid="keyword-role-all" onClick={() => setKeywordRole("all")}
                >All</Button>
                {(emergingKeywords?.available_roles || []).map((r) => (
                  <Button
                    key={r.id}
                    variant={keywordRole === r.id ? "default" : "outline"} size="sm"
                    className={`text-xs h-7 ${keywordRole === r.id ? "bg-[#194449] text-white" : ""}`}
                    data-testid={`keyword-role-${r.id}`} onClick={() => setKeywordRole(r.id)}
                  >{r.label}</Button>
                ))}
              </div>

              {(emergingKeywords?.top_keywords || []).length > 0 && (
                (emergingKeywords?.trend || []).length > 1 ? (
                  <div style={{ height: 220 }} className="mb-5" data-testid="keyword-trend-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={emergingKeywords.trend} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {emergingKeywords.top_keywords.map((kw, idx) => (
                          <Line key={kw} type="monotone" dataKey={kw} stroke={KEYWORD_TREND_COLORS[idx % KEYWORD_TREND_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-xs text-[#94A3B8] italic mb-4" data-testid="keyword-trend-insufficient-data">
                    Not enough history yet for a {keywordPeriod} trend — check back after a few more {keywordPeriod === "weekly" ? "weeks" : "months"} of activity.
                  </p>
                )
              )}

              {(emergingKeywords?.groups || []).length > 0 ? (
                <div className="space-y-5" data-testid="emerging-keywords-groups">
                  {emergingKeywords.groups.map((g) => (
                    <div key={g.solution_area} className="border border-black/5 rounded-lg p-4 bg-[#FBFBFC]" data-testid={`keyword-group-${g.solution_area}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                        <h4 className="text-sm font-semibold text-[#091F2C]">{g.solution_area_label}</h4>
                        <span className="text-xs text-[#64748B]">{g.sessions} session{g.sessions !== 1 ? "s" : ""} · {g.total_mentions} mention{g.total_mentions !== 1 ? "s" : ""}</span>
                      </div>
                      <p className="text-xs text-[#64748B] mb-3">
                        Roles asking: {(g.roles || []).map((r) => `${r.role_label} (${r.sessions})`).join(" · ") || "—"}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {g.keywords.map((k) => (
                          <span
                            key={k.keyword}
                            title={`${k.category_label}${(k.roles || []).length ? " — " + k.roles.map((r) => `${r.role_label}: ${r.count}`).join(", ") : ""}`}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border ${
                              k.category === "certification"
                                ? "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E3A8A]"
                                : k.category === "adjacent_tech"
                                ? "bg-[#FEF3C7] border-[#FDE68A] text-[#78350F]"
                                : k.category === "hero_product"
                                ? "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]"
                                : "bg-[#F4F3F5] border-black/5 text-[#091F2C]"
                            }`}
                            data-testid={`emerging-keyword-${k.keyword}`}
                          >
                            {k.keyword}
                            <span className="font-mono font-semibold">{k.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="flex flex-wrap gap-3 pt-1 text-[11px] text-[#64748B]">
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#EFF6FF] border border-[#BFDBFE]" /> Certification / Exam</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#ECFDF5] border border-[#A7F3D0]" /> Hero Product (tracked)</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#F4F3F5] border border-black/10" /> Microsoft Product</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#FEF3C7] border border-[#FDE68A]" /> Adjacent Technology</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#64748B] py-4 text-center" data-testid="emerging-keywords-empty">No product-relevant keyword requests for this period and filter.</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── Segmentation ── */}
        <TabsContent value="segmentation">
          <div className="space-y-6" data-testid="segmentation-panel">
            {[
              { key: "by_region", title: "By Region", icon: Building2 },
              { key: "by_partner", title: "By Partner", icon: Building2 },
              { key: "by_domain_type", title: "By Domain Type", icon: Target },
            ].map(({ key, title, icon: Icon }) => {
              const rows = segmentation?.[key] || [];
              const maxPlans = Math.max(1, ...rows.map(r => r.plans));
              return (
                <div key={key} className="bg-white rounded-xl border border-black/5 p-6 shadow-sm" data-testid={`segmentation-${key}`}>
                  <h3 className="font-medium text-[#091F2C] mb-4 flex items-center gap-2">
                    <Icon className="w-4 h-4 text-[#194449]" /> {title}
                  </h3>
                  {rows.length === 0 ? (
                    <p className="text-xs text-[#64748B]">No data for the selected period.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">{title.replace("By ", "")}</TableHead>
                          <TableHead className="text-xs text-right">Plans</TableHead>
                          <TableHead className="text-xs text-right">Learners</TableHead>
                          <TableHead className="text-xs text-right">Resource Clicks</TableHead>
                          <TableHead className="text-xs w-32">Share</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((r) => (
                          <TableRow key={r.name}>
                            <TableCell className="text-sm font-medium">{r.name}</TableCell>
                            <TableCell className="text-sm text-right font-mono">{r.plans}</TableCell>
                            <TableCell className="text-sm text-right font-mono">{r.learners}</TableCell>
                            <TableCell className="text-sm text-right font-mono">{r.clicks}</TableCell>
                            <TableCell>
                              <div className="h-2 bg-[#F4F3F5] rounded-full overflow-hidden">
                                <div className="h-full bg-[#194449] rounded-full" style={{ width: `${Math.round(r.plans / maxPlans * 100)}%` }} />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Drill-down dialog */}
      <DrillDownDialog
        open={drillDown.open}
        onClose={() => setDrillDown({ open: false, metric: null })}
        metric={drillDown.metric}
        region={regionFilter}
        roleBucket={roleFilter}
        days={parseInt(days)}
      />

      {/* Coverage-gap dialog */}
      <Dialog open={gapDialog.open} onOpenChange={(o) => setGapDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-2xl" data-testid="gap-suggestions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#194449]" />
              Suggested Content — {gapDialog.product?.display_name}
            </DialogTitle>
            <DialogDescription>
              LevelUp courses matching this product that are not in the library yet.
            </DialogDescription>
          </DialogHeader>
          {gapLoading ? (
            <div className="py-10 flex items-center justify-center text-[#64748B]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Finding suggestions...
            </div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto space-y-2">
              {(gapSuggestions?.suggestions || []).length === 0 ? (
                <div className="py-6 text-center space-y-3">
                  <p className="text-sm text-[#64748B]">{gapSuggestions?.message || "No suggestions found."}</p>
                  {gapSuggestions?.learn_search_url && (
                    <a href={gapSuggestions.learn_search_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#194449] hover:underline" data-testid="gap-learn-search-link">
                      <ExternalLink className="w-3.5 h-3.5" /> Search Microsoft Learn
                    </a>
                  )}
                </div>
              ) : (
                (gapSuggestions.suggestions).map((s) => (
                  <label key={s.resource_id} className="flex items-start gap-3 p-3 rounded-lg border border-black/5 hover:bg-[#F8FAF9] cursor-pointer" data-testid={`gap-suggestion-${s.resource_id}`}>
                    <Checkbox checked={!!gapSelected[s.resource_id]} onCheckedChange={(v) => setGapSelected((sel) => ({ ...sel, [s.resource_id]: !!v }))} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#091F2C] truncate">{s.title}</p>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {s.course_type && <Badge className="bg-[#E8F1EE] text-[#194449] border-0 text-[10px]">{s.course_type}</Badge>}
                        {(s.role_tags || []).slice(0, 2).map((rt) => (
                          <Badge key={rt} className="bg-[#F4F3F5] text-[#64748B] border-0 text-[10px]">{rt}</Badge>
                        ))}
                        {s.url && (
                          <a href={s.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[10px] text-[#194449] inline-flex items-center gap-1 hover:underline">
                            <ExternalLink className="w-3 h-3" /> Preview
                          </a>
                        )}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGapDialog({ open: false, product: null })} data-testid="gap-dialog-cancel">Cancel</Button>
            {(gapSuggestions?.suggestions || []).length > 0 && (
              <Button onClick={importGapSelected} disabled={gapImporting} className="bg-[#194449] hover:bg-[#0f2e31] text-white gap-1.5" data-testid="gap-import-btn">
                {gapImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Import &amp; Publish {Object.values(gapSelected).filter(Boolean).length || ""}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
